import { useEffect, useMemo } from "react";

import {
    SelectInput,
    SelectOptionGroupType,
} from "@impero-com/common/components/form/fields/selectInput";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { Checkbox } from "antd";
import moment from "moment-timezone";
import { Control, useController, useWatch } from "react-hook-form";

import {
    ControlWithFieldsResources,
    ControlScheduling,
} from "api/generated/model";

import { useFeatureFlag } from "../../../../utils/hooks/useFeatureFlag";
import { ControlViewFormSchema } from "../formSchema";
import { useTriggerCompatibleControlsQuery } from "../queries/useTriggeringCompatibleControlsQuery";

import * as theme from "./ControlTriggering.theme.css";

interface ControlTriggeringProps {
    control: Control<ControlViewFormSchema>;
    controlId: string | null;
    initialTriggeredByControlId: string | null;
    linkedControls: ControlWithFieldsResources["controls"];
    linkedControlPrograms: ControlWithFieldsResources["controlPrograms"];
    disabled?: boolean;
}

export const ControlTriggering: React.FC<ControlTriggeringProps> = ({
    control,
    controlId,
    initialTriggeredByControlId,
    linkedControls,
    linkedControlPrograms,
    disabled,
}) => {
    const { t } = useTranslation();

    const featureFlagEnabled = useFeatureFlag("controlTriggering");
    const { field } = useController({ control, name: "triggeredBy", disabled });
    const isCheckboxChecked = field.value !== null;

    // Build ControlScheduling for the query
    const scheduling = useWatch({ control, name: "scheduling" });
    const sendTime = useWatch({ control, name: "allocatedTimes.sendTime" });
    const controlScheduling: ControlScheduling = {
        timezoneName: scheduling.timezoneName,
        dueTime: moment(scheduling.dueTime).format("HH:mm"),
        sendTime: moment(sendTime).format("HH:mm"),
        startDate: moment(scheduling.startDate).format("YYYY-MM-DD"),
        rule: scheduling.rule,
        coveredPeriod: scheduling.coveredPeriod,
        remindersBefore: [],
        remindersAfter: [],
    };

    const { data, isLoading, refetch } = useTriggerCompatibleControlsQuery(
        {
            scheduling: controlScheduling,
            controlId,
        },
        false
    );

    useEffect(() => {
        if (isCheckboxChecked) {
            refetch();
        }
    }, [isCheckboxChecked, refetch]);

    const handleCheckboxChange = (checked: boolean) => {
        field.onChange(checked ? "" : null);
    };

    const selectOptions: SelectOptionGroupType<string>[] = useMemo(() => {
        const linkedSelectOptions: SelectOptionGroupType<string>[] = [];

        if (initialTriggeredByControlId !== null) {
            const initialControl = linkedControls.find(
                (c) => c.resource.id === initialTriggeredByControlId
            );
            if (!initialControl) {
                throw new Error(
                    `Could not find initial triggered by control with ID ${initialTriggeredByControlId} in linked controls.`
                );
            }
            const initialProgram = linkedControlPrograms.find(
                (program) =>
                    program.resource.id ===
                    initialControl.resource.controlProgramId
            );
            if (!initialProgram) {
                throw new Error(
                    `Could not find control program with ID ${initialControl.resource.controlProgramId} for initial triggered by control.`
                );
            }
            linkedSelectOptions.push({
                label: initialProgram.resource.name,
                key: initialProgram.resource.id,
                options: [
                    {
                        value: initialControl.resource.id,
                        label: initialControl.resource.title,
                        disabled: true,
                    },
                ],
            });
        }

        if (!data || isLoading) {
            return linkedSelectOptions;
        }

        return [
            ...data.linkedResources.programNames.map((programName) => ({
                label: programName.name,
                key: programName.id,
                options: [
                    ...data.content
                        .filter((c) => c.resource.programId === programName.id)
                        .map((c) => ({
                            value: c.resource.id,
                            label: c.resource.title,
                        })),
                    ...(linkedSelectOptions
                        .find((program) => program.key === programName.id)
                        ?.options.filter(
                            (option) =>
                                !data.content.some(
                                    (c) => c.resource.id === option.value
                                )
                        ) || []),
                ],
            })),
            ...linkedSelectOptions.filter(
                (program) =>
                    !data.linkedResources.programNames.some(
                        (listedProgram) => listedProgram.id === program.key
                    )
            ),
        ];
    }, [
        data,
        initialTriggeredByControlId,
        linkedControls,
        linkedControlPrograms,
        isLoading,
    ]);

    return (
        <div>
            {featureFlagEnabled && (
                <>
                    <Checkbox
                        checked={isCheckboxChecked}
                        onChange={(e) => handleCheckboxChange(e.target.checked)}
                        disabled={disabled}
                    >
                        {t("CreateControl_Recurrence_TriggerControl")}
                    </Checkbox>
                    {isCheckboxChecked && (
                        <SelectInput
                            showSearch
                            value={field.value || undefined}
                            onChange={(value) =>
                                field.onChange(
                                    value !== null && value !== undefined
                                        ? String(value)
                                        : null
                                )
                            }
                            onBlur={field.onBlur}
                            disabled={disabled || isLoading}
                            className={theme.triggeringControlSelect}
                            placeholder={t(
                                "CreateControl_Recurrence_TriggerControl_Dropdown_Placeholder"
                            )}
                            options={selectOptions}
                            filterOption={(input, option) => {
                                return (
                                    typeof option?.label === "string" &&
                                    option.label
                                        .toLowerCase()
                                        .includes(input.toLowerCase())
                                );
                            }}
                        />
                    )}
                </>
            )}
        </div>
    );
};
